import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Link, List, ListItem, ListItemIcon, ListItemText, Paper, Rating, Tab, Tabs, Typography } from '@mui/material';
import FilePresentIcon from '@mui/icons-material/FilePresent';
import LinkIcon from '@mui/icons-material/Link';
import PersonIcon from '@mui/icons-material/Person';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Footer from '../master/components/Footer';
import MasterAppBar from '../master/components/MasterAppBar';
import CourseFinalTest from '../Course/components/CourseFinalTest';
import { useAuthContext } from '../auth/hooks/useAuthContext';
import { buildBackendUrl } from '../../config/API';
import { fetchSessionDetails } from '../../services/sessionData';

const EmptyState = ({ children }) => <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>{children}</Typography>;

export default function Session() {
  const { id } = useParams();
  const { t } = useTranslation();
  const { user } = useAuthContext();
  const [tab, setTab] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadSession = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setData(await fetchSessionDetails(id, user?.token));
    } catch (requestError) {
      setError(requestError.response?.data?.message || t('something_went_wrong'));
    } finally {
      setLoading(false);
    }
  }, [id, t, user?.token]);

  useEffect(() => { loadSession(); }, [loadSession]);
  const session = data?.session;

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <MasterAppBar crumb />
      <Box component="main" sx={{ width: 'min(1120px, 100%)', mx: 'auto', px: { xs: 2, md: 5 }, py: 4, flex: 1 }}>
        {loading && <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 240 }}><CircularProgress /></Box>}
        {!loading && error && <Alert severity="error" action={<Button color="inherit" onClick={loadSession}>{t('retry')}</Button>}>{error}</Alert>}
        {!loading && !error && !session && <Alert severity="info">{t('no_data')}</Alert>}
        {!loading && !error && session && (
          <>
            <Typography variant="h3" component="h1" sx={{ mb: 1 }}>{session.title}</Typography>
            <Typography color="text.secondary" sx={{ mb: 2 }}>{session.session_brief || t('no_description')}</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, color: 'text.secondary', mb: 3 }}><Typography>{session.date || t('not_available')}</Typography><Typography>{session.start_time} - {session.end_time}</Typography></Box>
            <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
              <Tabs value={tab} onChange={(_, value) => setTab(value)} variant="scrollable" scrollButtons="auto"><Tab label={t('descussion')} /><Tab label={t('links')} /><Tab label={t('attachments')} /><Tab label={t('quiz')} /><Tab label={t('reviews')} /></Tabs>
              <Box sx={{ p: { xs: 2, md: 3 }, minHeight: 220 }}>
                {tab === 0 && (data.discussions.length ? data.discussions.map((comment) => { const author = comment.user_id?.[0]; return <Paper key={comment.id} variant="outlined" sx={{ p: 2, mb: 1.5 }}><Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}><PersonIcon fontSize="small" /><Typography fontWeight={600}>{author?.name || t('user')}</Typography></Box><Typography>{comment.message}</Typography><Typography variant="caption" color="text.secondary">{comment.upvoting || 0} {t('votes')}</Typography></Paper>; }) : <EmptyState>{t('no_comments')}</EmptyState>)}
                {tab === 1 && (data.links.length ? <List>{data.links.map((item, index) => { const href = item.link || item; return <ListItem key={item.id || href || index}><ListItemIcon><LinkIcon /></ListItemIcon><ListItemText primary={<Link href={href} target="_blank" rel="noreferrer">{href}</Link>} /></ListItem>; })}</List> : <EmptyState>{t('no_data')}</EmptyState>)}
                {tab === 2 && (data.files.length ? <List>{data.files.map((file) => <ListItem key={file.id}><ListItemIcon><FilePresentIcon /></ListItemIcon><ListItemText primary={<Link href={buildBackendUrl(file.file_path)} target="_blank" rel="noreferrer">{file.file_name || t('attachment')}</Link>} /></ListItem>)}</List> : <EmptyState>{t('no_data')}</EmptyState>)}
                {tab === 3 && (data.quizzes.length ? <CourseFinalTest data={data.quizzes} /> : <EmptyState>{t('no_data')}</EmptyState>)}
                {tab === 4 && <Box><Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}><Rating value={data.reviewAverage} precision={0.1} readOnly /><Typography>{data.reviewAverage.toFixed(1)}</Typography></Box>{data.reviews.length ? data.reviews.map((review) => <Paper key={review.review_id} variant="outlined" sx={{ p: 2, mb: 1.5 }}><Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}><Typography fontWeight={600}>{review.user_name}</Typography><Rating value={Number(review.stars_count || 0)} readOnly size="small" /></Box><Typography sx={{ my: 1 }}>{review.review_text}</Typography><Typography variant="caption" color="text.secondary">{review.date_time}</Typography></Paper>) : <EmptyState>{t('no_data')}</EmptyState>}</Box>}
              </Box>
            </Paper>
          </>
        )}
      </Box>
      <Footer />
    </Box>
  );
}
